import JSON5 from "json5";
import { MageXPatient } from "@prisma/client";
import { JsonArray } from "@prisma/client/runtime/library";
import type { TypeEnum } from "@app/types/few-schot-schema";
import type {
  FieldGesture,
  MageXPatientWithFeedbacks,
} from "@api/src/types/patient";

// Shared truth/aggregated resolution used by every place that has to display a
// patient field value: the ontology field rows (``ValueAndPropertyDisplay``) and
// the header badges (``PatientHeaderCard``). Keeping a single implementation
// guarantees a correction made on a field is reflected identically wherever that
// field is shown — the badge can no longer drift from the field it mirrors.

// Treats null / undefined / empty string / empty array as "no value", so
// clearing a multiselect (``[]``) is handled the same way as clearing a text
// input.
export const isFieldValueEmpty = (v: unknown): boolean =>
  v === undefined ||
  v === null ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);

// The words a boolean is written in, as the server reads them when it stores a gesture
// (``routers.patient._normalize_field_value``). `null` for anything else.
const TRUE_WORDS = new Set(["true", "1", "yes", "oui"]);
const FALSE_WORDS = new Set(["false", "0", "no", "non"]);
export const readBoolean = (raw: unknown): boolean | null => {
  if (raw === true || raw === false) return raw;
  if (typeof raw !== "string") return null;
  const word = raw.trim().toLowerCase();
  if (TRUE_WORDS.has(word)) return true;
  if (FALSE_WORDS.has(word)) return false;
  return null;
};

// Every shape of "nothing" a field value can take, folded into one predicate.
//
// Mirrors the STRUCTURAL half of ``utils/precision.means_nothing`` on the backend: null,
// undefined, a blank string, and a list whose members all mean nothing. The other half —
// the written absence tokens ("Aucune", "Aucun") — is deliberately NOT mirrored: that
// vocabulary is declared per field in the ontology's ``output_constraints`` and reading it
// here would create a second, disagreeing list, which is precisely the defect the backend
// docstring records having already paid for once.
export const meansNothing = (v: unknown): boolean => {
  if (v === undefined || v === null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.every(meansNothing);
  return false;
};

// Structural equality between two field values, with emptiness folded.
//
// Single implementation for the two questions that used to be answered by two different
// pieces of code: "did the clinician actually change anything" (the modal, which decides
// whether the gesture is a correction or a confirmation) and "does the validated truth
// disagree with the extraction" (the field row, which decides between a green check and a
// red cross). Those two must never disagree — a save recorded as a correction that then
// renders as an agreement is unreadable.
//
// Arrays compare without order, as the previous field-row implementation did, so
// reordering a multiselect is not a correction.
export function isSameFieldValue(a: unknown, b: unknown): boolean {
  if (meansNothing(a) && meansNothing(b)) return true;
  if (meansNothing(a) || meansNothing(b)) return false;
  if (a === b) return true;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((itemA) => b.some((itemB) => isSameFieldValue(itemA, itemB)));
  }
  if (Array.isArray(a) || Array.isArray(b)) return false;

  if (typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a as Record<string, unknown>);
    const keysB = Object.keys(b as Record<string, unknown>);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(b, key) &&
        isSameFieldValue(
          (a as Record<string, unknown>)[key],
          (b as Record<string, unknown>)[key],
        ),
    );
  }
  return false;
}

const cleanArrayUniqueValues = (items: JsonArray): JsonArray => {
  const setOfItems = new Set<string>();
  for (const item of items) {
    setOfItems.add(JSON.stringify(item));
  }
  return Array.from(setOfItems).map((item) => JSON.parse(item));
};

const extractStandaloneNumber = (raw: string): number | null => {
  const matches = raw.match(/(?<![A-Za-z])-?\d+(?:[.,]\d+)?(?![A-Za-z])/g);
  if (!matches || matches.length === 0) return null;
  const parsed = parseFloat(matches[matches.length - 1].replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeDateString = (raw: string): string => {
  const trimmed = raw.trim();
  const isoMatch = trimmed.match(/\d{4}-\d{2}-\d{2}/);
  if (isoMatch) return isoMatch[0];
  const dmySlashMatch = trimmed.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (dmySlashMatch) {
    const [, dd, mm, yyyy] = dmySlashMatch;
    return `${yyyy}-${mm}-${dd}`;
  }
  const dmyDashMatch = trimmed.match(/(\d{2})-(\d{2})-(\d{4})/);
  if (dmyDashMatch) {
    const [, dd, mm, yyyy] = dmyDashMatch;
    return `${yyyy}-${mm}-${dd}`;
  }
  return trimmed;
};

// Ontology types whose values must always reach the renderers as a list.
const ARRAY_TYPES: ReadonlySet<string> = new Set(["array_string", "array_enum"]);

// Ontology types whose value is ONE thing. ``scores`` is deliberately absent: a series holding a
// single dated point is still a series, and unwrapping it would hand the chart a bare object.
const SCALAR_TYPES: ReadonlySet<string> = new Set([
  "string",
  "number",
  "boolean",
  "date",
]);

// Wrap a scalar into a one-item list for array-typed fields.
//
// Why: feedbacks are an append-only log, so a value validated while the
// ontology still typed the field as a scalar stays a scalar forever — even
// after the field is retyped to ``array_string`` / ``array_enum``. Without
// this coercion the array renderers either fall through to "Non déterminé"
// or throw on ``.map`` of a string, and the validated value is lost from the
// patient page.
//
// ``null`` / ``undefined`` mean "absent" and are preserved as-is; an empty
// string is an empty list, not a list holding an empty string.
export const coerceToArray = (raw: unknown): unknown => {
  if (raw == null || Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed === "" ? [] : [trimmed];
  }
  return [raw];
};

// Unwrap a one-item list on a field the ontology declares scalar.
//
// The mirror of ``coerceToArray``, and it exists because the mirror drift exists: the care-pathway
// builder used to write one row per reality established at a moment and wrapped the value as soon
// as a second mention arrived, even one adding nothing. A boolean then reached the page as
// ``[true]``, which is truthy whatever it holds — so ``[false]`` read « Oui » too — and the green
// check, comparing against ``true``, recorded « Non » under a screen saying « Oui ». The builder
// no longer writes that shape; this is what makes the rows already written read correctly, and the
// display, the badge and the confirmation agree on one value rather than three.
//
// Only a SINGLE item is unwrapped. A scalar field genuinely holding several values (a patient's
// five treatments) keeps all of them: showing them is right, and picking one would be a silent
// deletion.
export const coerceToScalar = (raw: unknown): unknown =>
  Array.isArray(raw) && raw.length === 1 ? raw[0] : raw;

const normalizeByType = (raw: unknown, type?: TypeEnum): unknown => {
  if (type && ARRAY_TYPES.has(type)) {
    return coerceToArray(raw);
  }
  if (type && SCALAR_TYPES.has(type)) {
    raw = coerceToScalar(raw);
  }
  if (type === "number" && typeof raw === "string") {
    const n = extractStandaloneNumber(raw);
    return n !== null ? n : raw;
  }
  if (type === "date" && typeof raw === "string") {
    return normalizeDateString(raw);
  }
  if (type === "boolean") {
    // Case-insensitive: older feedbacks were persisted as Python's capital-case
    // "True"/"False". What no word of the server's reads stays as written.
    return readBoolean(raw) ?? raw;
  }
  return raw;
};

// The gesture that governs a row, as the server serves it beside the value (`GET /state` for the
// profile, `GET /pathway` for an episode).
//
// The page decides nothing about it. It used to: it elected the governing gesture on the correction
// log, once for a profile row and once for an episode row, and compared the gesture's value with the
// value shown to dash the badge. Neither election was the one that produced the value — the
// projection's, the pathway's — and the badges described values their rows did not show (PT-128,
// PT-133). Which gesture governs, and whether the value shown is still the one it judged, are now
// answered where the value is (`correction_election`), and only there.
export interface ServedGesture {
  verdict: FieldGesture;
  // The value the gesture judged, in the form its row shows a value.
  value: unknown;
  // The value shown is no longer the one judged: a later document stacked on it, or a derived value
  // was recomputed over it.
  superseded: boolean;
  // The clinical day the gesture is anchored on, and when it was saved — which names its log row.
  effective_date?: string | null;
  submitted_at?: string | null;
}

// The aggregated value for a field: what the extraction wrote, normalised the way the
// renderers expect it. Shared so the field row, the badges and the record-wide
// validation bar all read the same thing off the same row.
export function resolveAggregatedValue(
  patient: MageXPatientWithFeedbacks,
  property: keyof MageXPatient,
  type?: TypeEnum,
): unknown {
  // The aggregated column can hold a scalar where the ontology declares an array (same
  // legacy retype as the feedback log), and a one-item list where it declares a scalar (the
  // care-pathway wrapping described on ``coerceToScalar``). Both go through the coercion their
  // declaration calls for, HERE rather than in each caller: this function feeds the value on
  // screen, the value a confirmation records and the value a badge compares against, and those
  // three disagreeing is what let a record be validated as « Non » while reading « Oui ».
  const declared = ARRAY_TYPES.has(type ?? "")
    ? coerceToArray(patient?.[property])
    : SCALAR_TYPES.has(type ?? "")
      ? coerceToScalar(patient?.[property])
      : patient?.[property];
  return Array.isArray(declared) ? cleanArrayUniqueValues(declared) : declared;
}

// The empty value of a type, used as the ``old_value`` of a field the extraction left
// blank so the log never records `undefined`.
const getEmptyValue = (type?: TypeEnum): unknown => {
  switch (type) {
    case "scores":
    case "array_string":
    case "array_enum":
      return [];
    case "number":
      return 0;
    case "date":
      return "";
    case "boolean":
      return false;
    default:
      return "";
  }
};

const serializeFieldValue = (v: unknown): string =>
  typeof v === "string" ? v : JSON.stringify(v ?? "");

export interface FieldValidationBody {
  field: string;
  old_value: string;
  new_value: string;
  // The clinical day THIS confirmation describes: each value is dated by the journal, and
  // confirming a row confirms that fact — not the patient's state on the day of the click.
  effective_date?: string | null;
  // The ACT this confirmation speaks about, when the surface knows it. Same reason as the date, and
  // it does not duplicate it: an episode's bounds are an interval, so an anchor falling inside says
  // nothing about which of the two operations is meant. Filled in by an episode's sweep, each field
  // of which comes from a named moment's document.
  scope_doc_id?: string | null;
}

// What confirming ONE field posts: the aggregated value, typed and serialised.
//
// Single implementation on purpose. The per-field check button and the "validate the
// rest" bar must produce byte-identical bodies, because the backend writes the same log
// rows for both and nothing downstream can tell them apart — a divergence here would be
// a divergence in the clinical record itself.
//
// Absence is preserved rather than defaulted: confirming a "Non déterminé" sends an empty
// string, which reads back as null. Falling back to ``getEmptyValue()`` would persist a
// concrete answer (``false`` for a boolean, ``0`` for a number) that means something else
// entirely.
export function buildFieldValidation(
  patient: MageXPatientWithFeedbacks,
  property: keyof MageXPatient,
  type?: TypeEnum,
): { value: unknown; body: FieldValidationBody } {
  const aggregatedValue = resolveAggregatedValue(patient, property, type);
  let value: unknown = aggregatedValue;

  if (type === "number") {
    if (typeof aggregatedValue === "number") {
      value = aggregatedValue;
    } else if (typeof aggregatedValue === "string") {
      const n = extractStandaloneNumber(aggregatedValue);
      value = n ?? aggregatedValue.trim();
    }
  } else if (type === "date" && typeof aggregatedValue === "string") {
    value = normalizeDateString(aggregatedValue);
  } else if (type === "boolean") {
    // Preserve absence: confirming a "Non déterminé" aggregated value must not coerce
    // null/undefined to `false` — that would flip the truth to "Non".
    //
    // And never ANSWER for a shape this cannot read. ``x === true`` says "Non" of everything that
    // is not exactly ``true``, so the ``[true]`` the care pathway used to write — which the page
    // renders « Oui » — was confirmed as « Non ». Seen in production on an orthopaedics record:
    // a green check on "Drainage chirurgical : Oui" recorded ``false``, and the badge then reported
    // a value moving under the gesture that nothing had moved. The reading is the module's own, so
    // what the screen shows as Oui/Non is what gets written down.
    const read = normalizeByType(aggregatedValue, "boolean");
    value = read === undefined ? null : read;
  } else if (
    type === "array_string" ||
    type === "array_enum" ||
    type === "scores"
  ) {
    // Only a string is a serialized list. An absent field stays absent: `JSON5.parse(undefined)`
    // reads the text "undefined" and throws.
    if (typeof aggregatedValue === "string") {
      try {
        value = JSON5.parse(aggregatedValue);
      } catch (error) {
        console.warn("Error parsing JSON:", error, aggregatedValue);
        value = aggregatedValue;
      }
    }
  }

  const isAbsent = value === null || value === undefined;
  return {
    value,
    body: {
      field: String(property),
      old_value: serializeFieldValue(patient[property] ?? getEmptyValue(type)),
      new_value: isAbsent ? "" : serializeFieldValue(value),
    },
  };
}
